import { createTrackingEmitterHooks } from "@b2bportal/core-tracking";
import type {
  SupportHubCommonProperties,
  SupportHubDismissedProperties,
  SupportHubHelpLaunchedProperties,
  SupportHubViewedProperties,
} from "./supportHub";

export enum SelfServeEventName {
  confirm_resend_email = "confirm_resend_email",
  viewed_resend_email = "viewed_resend_email",
  support_hub_help_launched = "support_hub_help_launched",
  support_hub_viewed = "support_hub_viewed",
  support_hub_tapped_manage_booking = "support_hub_tapped_manage_booking",
  support_hub_tapped_faq = "support_hub_tapped_faq",
  support_hub_tapped_contact_support = "support_hub_tapped_contact_support",
  support_hub_dismissed = "support_hub_dismissed",
}

export enum SelfServeRawEventName {
  ConfirmResendEmail = "ConfirmResendEmail",
  ViewedResendEmailModal = "ViewedResendEmailModal",
  SupportHubHelpLaunched = "SupportHubHelpLaunched",
  SupportHubViewed = "SupportHubViewed",
  SupportHubTappedManageBooking = "SupportHubTappedManageBooking",
  SupportHubTappedFaq = "SupportHubTappedFaq",
  SupportHubTappedContactSupport = "SupportHubTappedContactSupport",
  SupportHubDismissed = "SupportHubDismissed",
}

export type SelfServeConfirmResendEmailRawEvent = {
  name: SelfServeRawEventName.ConfirmResendEmail;
  properties: Record<string, unknown>;
};

export type SelfServeViewedResendEmailModalRawEvent = {
  name: SelfServeRawEventName.ViewedResendEmailModal;
  properties: Record<string, unknown>;
};

export type SupportHubHelpLaunchedRawEvent = {
  name: SelfServeRawEventName.SupportHubHelpLaunched;
  properties: SupportHubHelpLaunchedProperties;
};

export type SupportHubViewedRawEvent = {
  name: SelfServeRawEventName.SupportHubViewed;
  properties: SupportHubViewedProperties;
};

export type SupportHubTappedManageBookingRawEvent = {
  name: SelfServeRawEventName.SupportHubTappedManageBooking;
  properties: SupportHubCommonProperties;
};

export type SupportHubTappedFaqRawEvent = {
  name: SelfServeRawEventName.SupportHubTappedFaq;
  properties: SupportHubCommonProperties;
};

export type SupportHubTappedContactSupportRawEvent = {
  name: SelfServeRawEventName.SupportHubTappedContactSupport;
  properties: SupportHubCommonProperties;
};

export type SupportHubDismissedRawEvent = {
  name: SelfServeRawEventName.SupportHubDismissed;
  properties: SupportHubDismissedProperties;
};

export type SupportHubRawEvent =
  | SupportHubHelpLaunchedRawEvent
  | SupportHubViewedRawEvent
  | SupportHubTappedManageBookingRawEvent
  | SupportHubTappedFaqRawEvent
  | SupportHubTappedContactSupportRawEvent
  | SupportHubDismissedRawEvent;

export type SupportHubRawEventName = SupportHubRawEvent["name"];

export type SelfServeRawEvent =
  | SelfServeConfirmResendEmailRawEvent
  | SelfServeViewedResendEmailModalRawEvent
  | SupportHubRawEvent;

export type SupportHubHelpLaunchedEvent = {
  name: SelfServeEventName.support_hub_help_launched;
  properties: SupportHubHelpLaunchedProperties;
};

export type SupportHubViewedEvent = {
  name: SelfServeEventName.support_hub_viewed;
  properties: SupportHubViewedProperties;
};

export type SupportHubTappedManageBookingEvent = {
  name: SelfServeEventName.support_hub_tapped_manage_booking;
  properties: SupportHubCommonProperties;
};

export type SupportHubTappedFaqEvent = {
  name: SelfServeEventName.support_hub_tapped_faq;
  properties: SupportHubCommonProperties;
};

export type SupportHubTappedContactSupportEvent = {
  name: SelfServeEventName.support_hub_tapped_contact_support;
  properties: SupportHubCommonProperties;
};

export type SupportHubDismissedEvent = {
  name: SelfServeEventName.support_hub_dismissed;
  properties: SupportHubDismissedProperties;
};

export type SupportHubHTSEvent =
  | SupportHubHelpLaunchedEvent
  | SupportHubViewedEvent
  | SupportHubTappedManageBookingEvent
  | SupportHubTappedFaqEvent
  | SupportHubTappedContactSupportEvent
  | SupportHubDismissedEvent;

// Add the transformed events for SelfServeRawEventName.ViewedResendEmailModal and SelfServeRawEventName.ConfirmResendEmail here
export type SelfServeHTSEvent = SupportHubHTSEvent;

export const {
  useEmitRawEvent: useEmitCoreSelfServeRawEvent,
  useEmitRawEventOnce: useEmitCoreSelfServeRawEventOnce,
} = createTrackingEmitterHooks<SelfServeRawEvent>();
